<template>
  <page-wrapper error-key="legal" transparent-bg :breadcrumbs="breadcrumbs">
    <template #title>
      <div v-if="!activeLegal">
        <skeleton-loader type="h3" width="220px" />
        <skeleton-loader height="21px" width="150px" class="mt-1" />
      </div>
      <div v-else>
        <div class="h3">{{ activeLegal.data.name }}</div>
        <status-string
          class="mt-1"
          :text="LEGAL_APPROVE_STATUS_MAP[activeLegal.status].name"
          :variant="LEGAL_APPROVE_STATUS_MAP[activeLegal.status].color"
        />
      </div>
    </template>

    <page-block>
      <e-alert variant="info">
        <div class="paragraph-m_h font-600">
          Изменить данные Юридического лица вы можете обратившись к&nbsp;нашим менеджерам
        </div>
        <div class="paragraph-s_h text-secondary mt-1">
          Свяжитесь с
          <router-link :to="{ name: RouteNames.Support }" class="link">Поддержкой</router-link>
        </div>
      </e-alert>

      <property-block class="mt-6">
        <template #text>
          <div class="paragraph-l font-600">Общая информация</div>
        </template>
        <template #content>
          <div class="d-flex align-center py-2">
            <e-icon name="check" class="text-primary" size="20" />
            <skeleton-loader v-if="!activeLegal" height="16px" width="210px" class="ml-1" />
            <span v-else class="paragraph-s ml-1">
              {{ activeLegal.data.isIndividual ? 'Индивидуальный предприниматель' : 'Юридическое лицо' }}
            </span>
          </div>
          <property-item class="mt-4" title="Наименование">
            <skeleton-loader v-if="!activeLegal" type="paragraph-m_h" width="150px" class="ml-1" />
            <multilanguage-text v-else :text="activeLegal.data.nameML || []">
              <template #default="{ value }">
                <span>{{ value || activeLegal.data.name }}</span>
              </template>
            </multilanguage-text>
          </property-item>
          <div class="d-flex flex-wrap">
            <property-item class="mt-4 mr-10" title="ИНН">
              <skeleton-loader v-if="!activeLegal" type="paragraph-m_h" width="70px" class="ml-1" />
              <span v-else> {{ activeLegal.data.details.tin }}</span>
            </property-item>
            <property-item v-if="!activeLegal || activeLegal?.data.details.kpp" class="mt-4 mr-10" title="КПП">
              <skeleton-loader v-if="!activeLegal" type="paragraph-m_h" width="70px" class="ml-1" />
              <span v-else> {{ activeLegal.data.details.kpp }}</span>
            </property-item>
            <property-item v-if="!activeLegal || activeLegal?.data.details.ogrn" class="mt-4" title="ОГРН / ОГРНИП">
              <skeleton-loader v-if="!activeLegal" type="paragraph-m_h" width="70px" class="ml-1" />
              <span v-else> {{ activeLegal.data.details.ogrn }}</span>
            </property-item>
          </div>
          <property-item class="mt-4" title="Юридический адрес">
            <skeleton-loader v-if="!activeLegal" type="paragraph-m_h" width="150px" class="ml-1" />
            <multilanguage-text v-else :text="activeLegal.data.addressML || []">
              <template #default="{ value }">
                <span>{{ value || activeLegal.data.address }}</span>
              </template>
            </multilanguage-text>
          </property-item>
          <div v-if="!activeLegal || activeLegal?.data.isNotTaxPayer" class="d-flex align-center mt-6 mb-2">
            <e-icon name="check" class="text-primary" size="20" />
            <skeleton-loader v-if="!activeLegal" height="16px" width="210px" class="ml-1" />
            <span v-else class="paragraph-s ml-1"> Не является плательщиком НДС </span>
          </div>
        </template>
      </property-block>
    </page-block>

    <page-block class="mt-4">
      <property-block>
        <template #text>
          <div class="paragraph-l font-600">Документооборот</div>
          <div class="paragraph-s_h text-secondary mt-2">
            Выберите предпочтительный для Вас вариант обмена документами
          </div>
        </template>
        <template #content>
          <property-item v-if="!isEditEdiType" title="Вид документооборота">
            <skeleton-loader v-if="!legalEdiType" type="paragraph-m_h" width="150px" />
            <span v-else> {{ EDI_TYPE_SELECT_ITEMS.find((item) => item.value === legalEdiType)?.name }}</span>
          </property-item>
          <e-select
            v-else
            v-model="legalEdiType"
            :items="EDI_TYPE_SELECT_ITEMS"
            label="Вид документооборота"
            item-title="name"
            item-value="value"
          />

          <div v-if="legalEdiType === EdiType.Other" class="paragraph-m_h mt-4">
            Вы сможете просматривать документы, но формат обмена и их подписание вы осуществляете вне вашего
            пространства Экстранет
          </div>

          <template v-else>
            <div class="paragraph-m_h mt-4">
              Вам будет доступно подписание документов в разделе «Документы».
              <span class="link" @click="toggleTour(true)">Подробнее</span>
            </div>

            <tour
              v-if="isTourOpen"
              title="Чтобы подписывать документы в Экстранет, вам необходимо:"
              static
              class="mt-4"
              max-width="640px"
              @close="toggleTour(false)"
            >
              <template #text>
                <ol class="ol-list text-black">
                  <li>Установить программное обеспечение для работы с ЭЦП КриптоПроCSP</li>
                  <li>
                    Использовать для подписания документов
                    <a href="https://browser.yandex.ru/" target="_blank" rel="noopener noreferrer" class="link"
                      >Яндекс.Браузер</a
                    >
                  </li>
                  <li>
                    Установить плагин
                    <a
                      href="https://docs.cryptopro.ru/cades/plugin/plugin-installation-windows?id=%D0%A3%D1%81%D1%82%D0%B0%D0%BD%D0%BE%D0%B2%D0%BA%D0%B0-%D1%80%D0%B0%D1%81%D1%88%D0%B8%D1%80%D0%B5%D0%BD%D0%B8%D1%8F-%D0%B1%D1%80%D0%B0%D1%83%D0%B7%D0%B5%D1%80%D0%B0"
                      class="link"
                      target="_blank"
                      rel="noopener noreferrer"
                      >КриптоПро Browser Plug-in</a
                    >
                  </li>
                  <li>Установить токен с закрытым ключом в usb порт компьютера</li>
                  <li>Установить сертификаты в личные сертификаты пользователя</li>
                </ol>
              </template>
            </tour>
          </template>
        </template>

        <template #right>
          <operation-button
            v-if="!isEditEdiType"
            variant="primary"
            prepend-icon-src="editPen"
            :operation="activeLegal?.operations.canUpdateEDI"
            @click="handleEditEdiType"
          >
            Редактировать
          </operation-button>
          <div v-else class="d-flex flex-wrap flex-lg-column row-gap-4 col-gap-4">
            <confirmation-dialog confirm-text="Изменить" :action="handleEdiTypeUpdate">
              <template #activator>
                <e-btn variant="primary" :loading="isEdiTypeUpdateLoading"> Сохранить </e-btn>
              </template>

              <div class="h3 mb-6">Вы уверены, что хотите изменить вид документооборота?</div>
              <p class="paragraph-l text-secondary">
                Изменение вида документооборота повлечет за собой изменение в процессе обмена документами с Алеан.
              </p>
            </confirmation-dialog>
            <e-btn variant="secondary" @click="handleEdiTypeCancel"> Отмена </e-btn>
          </div>
        </template>
      </property-block>
    </page-block>
  </page-wrapper>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import type { BreadcrumbsItem } from '@/core/types/components/helps';
import OperationButton from '@/core/components/buttons/OperationButton.vue';
import StatusString from '@/core/components/common/StatusString.vue';
import Tour from '@/core/components/common/Tour.vue';
import PageBlock from '@/core/components/interface/PageBlock.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import PropertyBlock from '@/core/components/interface/PropertyBlock.vue';
import PropertyItem from '@/core/components/interface/PropertyItem.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import ConfirmationDialog from '@/core/components/modals/ConfirmationDialog.vue';
import { EAlert, EBtn, EIcon, ESelect, MultilanguageText } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { EdiType } from '@/graphql/generated';
import { useLegalsStore } from '@/parts/legals/store/legals';
import { EDI_TYPE_SELECT_ITEMS, LEGAL_APPROVE_STATUS_MAP } from '@/parts/legals/utils/consts';

const $route = useRoute();
const { activeLegal, getLegal, saveLegalEDIType } = useLegalsStore();

const isEditEdiType = ref(false);
const legalEdiType = ref();
const isEdiTypeUpdateLoading = ref(false);
const isTourOpen = ref(false);

const toggleTour = (value: boolean) => {
  isTourOpen.value = value;
};

const handleEditEdiType = () => {
  isEditEdiType.value = true;
};

const handleEdiTypeCancel = () => {
  isEditEdiType.value = false;
  legalEdiType.value = activeLegal.value?.data.ediType;
};

const handleEdiTypeUpdate = async () => {
  if (!legalEdiType.value || !activeLegal.value) {
    return;
  }

  isEdiTypeUpdateLoading.value = true;

  await saveLegalEDIType({
    legalCID: activeLegal.value.cid,
    ediType: legalEdiType.value,
  });

  isEdiTypeUpdateLoading.value = false;
  isEditEdiType.value = false;
  legalEdiType.value = activeLegal.value?.data.ediType;
};

const legalCid = computed(() => String($route.params.id));

const breadcrumbs = computed<BreadcrumbsItem[]>(() => [
  {
    icon: 'briefcase',
    title: 'Юридические лица',
    to: {
      name: RouteNames.Legals,
    },
  },
  {
    title: activeLegal.value?.data.name ?? undefined,
  },
]);

useFetch(async () => {
  await getLegal(legalCid.value);

  legalEdiType.value = activeLegal.value?.data.ediType;
});
</script>
